// 試合画面 (= DDSIM-18、 ADR-0025 §B.3)。
//
// mount で実 catalog の new_match を await → EngineHandle 取得 → snapshot(viewer=0)
// を state に。 操作ボタンを押すたびに apply → snapshot 再取得 → 再描画。
// EngineHandle は opaque handle なので useRef で保持し再生成しない (= ADR-0027 §B)。
//
// Deck 画面から location.state.cards (40枚の card_id_str[]) を受け取る。
// 直 URL アクセス時は SAMPLE_DECKS[0].cards を fallback にする。
//
// Action は engine の serde 既定 (= externally-tagged) に合わせた JS object:
//   ConfirmReady { player } -> { ConfirmReady: { player: "P1" } } (= PlayerSlot は "P1"/"P2")
//   PhaseAdvance            -> "PhaseAdvance" (= unit variant は bare 文字列)
//   Summon { card, cost }   -> { Summon: { card: <number>, cost: <catalog cost> } }
// card は InstanceId(u64) = bare number。 snapshot の instance_id は string 化されている
// (= engine-wasm convert.rs) ため Number() で戻す。 cost は catalog (card_id -> cost) から引く
// (= snapshot CardView は cost を持たないため client 側 catalog で補完)。

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { CardDefDto } from "@/bindings/CardDefDto";
import type { CardView } from "@/bindings/CardView";
import type { MatchSnapshot } from "@/bindings/MatchSnapshot";
import { Board } from "@/components/match/Board";
import { CardDetail } from "@/components/match/CardDetail";
import { CardCatalogProvider } from "@/components/match/cardCatalog";
import { LogPanel } from "@/components/match/LogPanel";
import { MatchResult } from "@/components/match/MatchResult";
import { Button } from "@/components/ui/button";
import { loadCatalog } from "@/lib/catalog";
import type { EngineHandle } from "@/lib/engine";
import { createSoloMatch } from "@/lib/match";
import { SAMPLE_DECKS } from "@/lib/sampleDecks";

type Status = "loading" | "ready" | "error";

const VIEWER_P1 = 0;
// SOLO は P1=人間 / P2=CPU 固定 (= DDSIM-23)。
const CPU_SLOT = 1;

interface LocationState {
  deckId?: string;
  cards?: string[];
  firstPlayer?: number;
}

export function Match(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const handleRef = useRef<EngineHandle | null>(null);
  // card_id -> CardDefDto。 summon の cost 引数と CardDetail の詳細表示に使う。
  const [catalogById, setCatalogById] = useState<Map<number, CardDefDto>>(
    new Map(),
  );
  const [snapshot, setSnapshot] = useState<MatchSnapshot | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  // 行動ログ (= client 側で組み立て、 LogPanel に渡す)。
  const [log, setLog] = useState<string[]>([]);
  // RETRY で値を増やすと effect が再走 (= 旧 handle を free → 同条件で再 createSoloMatch)。
  const [restartCount, setRestartCount] = useState<number>(0);

  const appendLog = (text: string): void => setLog((l) => [...l, text]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setLog([]);

    const state = location.state as LocationState | null;
    const deckCards = state?.cards ?? SAMPLE_DECKS[0].cards;
    // firstPlayer は Practice 画面で決定。直 URL アクセス時は 0 (先攻) を fallback。
    const firstPlayer = state?.firstPlayer ?? 0;

    void (async () => {
      try {
        const catalog = await loadCatalog();
        const handle = await createSoloMatch(deckCards, firstPlayer);
        if (cancelled) {
          handle.free();
          return;
        }
        setCatalogById(new Map(catalog.map((c) => [c.card_id, c])));
        handleRef.current = handle;
        // mount 直後: CPU (P2) が WaitingForReady 未確認なら setup を先行実行。
        // これで P1 が画面を開いた時点で CPU の初期 charge が完了している。
        try {
          const initSnap = handle.snapshot(VIEWER_P1) as MatchSnapshot;
          if (
            initSnap.match_phase === "WaitingForReady" &&
            initSnap.outcome === "Ongoing" &&
            !initSnap.players[1]!.ready_confirmed
          ) {
            handle.cpu_take_turn(CPU_SLOT);
          }
        } catch {
          // setup 先行失敗は無視 (= P1 の操作で再試行される)
        }
        setSnapshot(handle.snapshot(VIEWER_P1) as MatchSnapshot);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      handleRef.current?.free();
      handleRef.current = null;
    };
  }, [location.state, restartCount]);

  // action を apply して snapshot を再取得する (= 失敗時は error 表示、 内部 State は不変)。
  function dispatch(...actions: unknown[]): void {
    const handle = handleRef.current;
    if (handle === null) return;
    setBusy(true);
    setError(null);
    try {
      for (const action of actions) {
        handle.apply(action);
      }
      // P1 の手を適用後、 CPU (P2) が WaitingForReady 中なら setup を進める。
      // InGame 中の P2 手番も同様に 1 ターン消化。
      let snap = handle.snapshot(VIEWER_P1) as MatchSnapshot;
      const needsCpuSetup =
        snap.match_phase === "WaitingForReady" &&
        snap.outcome === "Ongoing" &&
        !snap.players[1]!.ready_confirmed;
      const needsCpuTurn =
        snap.match_phase === "InGame" &&
        snap.outcome === "Ongoing" &&
        snap.current_player === CPU_SLOT;
      if (needsCpuSetup || needsCpuTurn) {
        handle.cpu_take_turn(CPU_SLOT);
        snap = handle.snapshot(VIEWER_P1) as MatchSnapshot;
        appendLog(needsCpuSetup ? "CPU setup 完了" : "CPU ターン消化");
      }
      setSnapshot(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // P1 の ConfirmReady を送る (= P2 は ME-5 の CPU 自走で setup 完了済)。
  function handleReady(): void {
    appendLog("P1 READY");
    dispatch({ ConfirmReady: { player: "P1" } });
  }

  // WaitingForReady 中に P1 が手札カードを選んで初期 charge を配置する (= ADR-0030 §B)。
  function handleSetInitialCharge(card: CardView): void {
    appendLog(`P1 初期 charge: ${card.name ?? "?"}`);
    dispatch({ SetInitialCharge: { player: "P1", card: Number(card.instance_id) } });
  }

  // Charge phase に手札 1 枚を charge zone に face_up + untapped で送る (= ADR-0030 §C)。
  function handleCharge(card: CardView): void {
    appendLog(`P1 charge: ${card.name ?? "?"}`);
    dispatch({ Charge: { card: Number(card.instance_id) } });
  }

  function handlePhaseAdvance(): void {
    appendLog("P1 フェーズ進行");
    dispatch("PhaseAdvance");
  }

  // hand のカードを catalog 由来の cost で召喚 (= instance_id 文字列を InstanceId(u64) に戻す)。
  function handleSummon(card: CardView): void {
    const cost = catalogById.get(card.card_id)?.cost ?? 1;
    appendLog(`P1 召喚: ${card.name ?? "?"}`);
    dispatch({ Summon: { card: Number(card.instance_id), cost } });
  }

  // 攻撃宣言 (= Battle phase + 自手番で attacker → target / ダイレクト攻撃)。
  // AttackDeclare { attacker: InstanceId, target: Option<InstanceId> }
  // serde externally-tagged: { AttackDeclare: { attacker: <u64>, target: <u64|null> } }
  function handleAttack(attacker: CardView, target: CardView | null): void {
    appendLog(
      `P1 攻撃: ${attacker.name ?? "?"} → ${target?.name ?? "直接"}`,
    );
    dispatch({
      AttackDeclare: {
        attacker: Number(attacker.instance_id),
        target: target !== null ? Number(target.instance_id) : null,
      },
    });
  }

  // RETRY: 同じデッキ / 手番で試合をやり直す (= effect 再走で handle 再生成)。
  function handleRetry(): void {
    setRestartCount((c) => c + 1);
  }

  return (
    <main className="min-h-screen bg-background p-3">
      <div className="mx-auto max-w-4xl space-y-2">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-tight">
            試合 (SOLO)
          </h1>
          <Button variant="ghost" size="sm" onClick={() => navigate("/practice")}>
            ← プラクティス
          </Button>
        </div>

        {status === "loading" && (
          <p className="text-sm text-muted-foreground">engine 初期化中...</p>
        )}

        {status === "error" && (
          <p className="text-sm text-destructive">
            {error ?? "試合の初期化に失敗しました"}
          </p>
        )}

        {status === "ready" && snapshot !== null && (
          <CardCatalogProvider byId={catalogById}>
            <Board
              snapshot={snapshot}
              onReady={handleReady}
              onPhaseAdvance={handlePhaseAdvance}
              onSummon={handleSummon}
              onAttack={handleAttack}
              onSetInitialCharge={handleSetInitialCharge}
              onCharge={handleCharge}
              busy={busy}
            />
            <CardDetail />
            <LogPanel entries={log} />
            {error !== null && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            {snapshot.outcome !== "Ongoing" && (
              <MatchResult
                snapshot={snapshot}
                onRetry={handleRetry}
                onMenu={() => navigate("/")}
              />
            )}
          </CardCatalogProvider>
        )}
      </div>
    </main>
  );
}
